import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { ProductService } from 'proto/gen/ts/shirt/v1/product_pb';
import { Link } from 'react-router-dom';
import { ROUTES } from '../routes';
import { formatPrice } from '../utils/format';
import { transport } from '../utils/transport';
const productClient = createClient(ProductService, transport);
const FRAUNCES_DISPLAY_BOLD = {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 144, "SOFT" 50'
};
const FRAUNCES_DISPLAY_ITALIC = {
    fontWeight: 500,
    fontStyle: 'italic',
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 144, "SOFT" 100'
};
const FRAUNCES_MEDIUM = {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 96, "SOFT" 50'
};
/**
 * Elevated brand homepage. Editorial, not a catalog grid — each section does one job:
 * hero statement, manifesto, catalog list, process panel, closer.
 */ export default function LandingPage() {
    const { data, isLoading } = useQuery({
        queryKey: [
            'products',
            'featured'
        ],
        queryFn: ()=>productClient.listProducts({
                featuredOnly: true,
                limit: 6
            })
    });
    const products = data?.products ?? [];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("section", {
                className: "relative bg-bone-50 overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": true,
                        className: "absolute inset-0 pointer-events-none",
                        style: {
                            background: 'radial-gradient(ellipse 60% 50% at 90% 15%, rgba(162, 88, 31, 0.15), transparent 65%), radial-gradient(ellipse 40% 30% at 10% 90%, rgba(220, 132, 32, 0.08), transparent 60%)'
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-36 lg:pb-32",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-3 mb-10",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "h-px w-10 bg-clay-500",
                                        "aria-hidden": true
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-mono text-[10px] uppercase tracking-[0.35em] text-clay-600 font-medium",
                                        children: "Est. 2026 · Made in-house"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("h1", {
                                className: "font-display text-obsidian-900 text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-5xl",
                                style: FRAUNCES_DISPLAY_BOLD,
                                children: [
                                    "A small catalog",
                                    /*#__PURE__*/ _jsx("br", {}),
                                    "of",
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-clay-600",
                                        style: FRAUNCES_DISPLAY_ITALIC,
                                        children: "elevated"
                                    }),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    "essentials."
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mt-10 max-w-xl text-obsidian-700 text-lg leading-[1.65]",
                                children: "Premium smoking accessories designed for better sessions, cleaner routines, and a smoother everyday carry. Every product we release has to earn its place — or it doesn't launch."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mt-12 flex flex-wrap items-center gap-4",
                                children: [
                                    /*#__PURE__*/ _jsxs(Link, {
                                        to: ROUTES.shop,
                                        className: "group inline-flex items-center gap-3 bg-obsidian-900 text-bone-50 pl-6 pr-5 py-4 text-[11px] font-medium uppercase tracking-[0.3em] hover:bg-clay-600 transition-colors",
                                        children: [
                                            "Enter the catalog",
                                            /*#__PURE__*/ _jsx("span", {
                                                "aria-hidden": true,
                                                className: "inline-block translate-x-0 group-hover:translate-x-1 transition-transform",
                                                children: "→"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: ROUTES.about,
                                        className: "inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-obsidian-700 hover:text-clay-600 py-4 px-2 transition-colors",
                                        children: "Our process"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "bg-bone-100 border-y border-dune-200/60",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-4xl mx-auto px-6 sm:px-10 py-24 lg:py-32 text-center",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "font-mono text-[10px] uppercase tracking-[0.35em] text-ember-500 font-medium mb-10",
                            children: "Manifesto"
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "font-display text-obsidian-900 text-3xl sm:text-4xl lg:text-5xl leading-[1.15]",
                            style: FRAUNCES_DISPLAY_ITALIC,
                            children: [
                                "We choose fewer products. Better details.",
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-clay-600",
                                    style: FRAUNCES_DISPLAY_BOLD,
                                    children: "Higher standards."
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "bg-bone-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-end justify-between gap-6 mb-14",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-3 mb-5",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "h-px w-8 bg-clay-500",
                                                    "aria-hidden": true
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "font-mono text-[10px] uppercase tracking-[0.35em] text-clay-600 font-medium",
                                                    children: "The catalog"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "font-display text-obsidian-900 text-4xl sm:text-5xl leading-[0.95]",
                                            style: FRAUNCES_DISPLAY_BOLD,
                                            children: "Essentials, in order."
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Link, {
                                    to: ROUTES.shop,
                                    className: "hidden sm:inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-600 hover:text-clay-600 transition-colors pb-2",
                                    children: [
                                        "See all ",
                                        /*#__PURE__*/ _jsx("span", {
                                            "aria-hidden": true,
                                            children: "→"
                                        })
                                    ]
                                })
                            ]
                        }),
                        isLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "space-y-0",
                            children: Array.from({
                                length: 2
                            }).map((_, i)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-8 border-t border-dune-200/60 py-10 animate-pulse",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "w-16 h-10 bg-dune-200/60"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex-1 space-y-3",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "h-6 bg-dune-200/60 w-1/3"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "h-4 bg-dune-200/60 w-2/3"
                                                })
                                            ]
                                        })
                                    ]
                                }, `skel-${String(i)}`))
                        }) : products.length === 0 ? /*#__PURE__*/ _jsx("div", {
                            className: "border-t border-b border-dune-200/60 py-16 text-center",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "font-mono text-[11px] uppercase tracking-[0.3em] text-obsidian-500",
                                children: "Nothing ships yet — back soon."
                            })
                        }) : /*#__PURE__*/ _jsx("ol", {
                            className: "border-t border-dune-200/60",
                            children: products.map((p, idx)=>/*#__PURE__*/ _jsx("li", {
                                    className: "border-b border-dune-200/60",
                                    children: /*#__PURE__*/ _jsxs(Link, {
                                        to: `/shop/${p.slug}`,
                                        className: "group grid grid-cols-12 gap-6 items-baseline py-10 lg:py-12 transition-colors",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "col-span-2 lg:col-span-1 font-display text-clay-500 text-3xl lg:text-5xl group-hover:text-clay-600 transition-colors",
                                                style: FRAUNCES_DISPLAY_ITALIC,
                                                children: String(idx + 1).padStart(2, '0')
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "col-span-10 lg:col-span-7",
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "font-display text-obsidian-900 text-3xl lg:text-5xl leading-[0.95] group-hover:text-clay-600 transition-colors",
                                                        style: FRAUNCES_DISPLAY_BOLD,
                                                        children: p.name
                                                    }),
                                                    p.description && /*#__PURE__*/ _jsx("p", {
                                                        className: "mt-3 text-obsidian-600 text-base lg:text-lg leading-[1.55] max-w-xl",
                                                        children: p.description
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "col-span-6 lg:col-span-3 lg:text-right",
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    className: "font-display text-obsidian-900 text-xl lg:text-2xl",
                                                    style: FRAUNCES_MEDIUM,
                                                    children: formatPrice(p.priceCents)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "col-span-6 lg:col-span-1 lg:text-right",
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    "aria-hidden": true,
                                                    className: "inline-flex items-center justify-end gap-1 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-500 group-hover:text-clay-600 transition-colors",
                                                    children: [
                                                        "View",
                                                        ' ',
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "inline-block translate-x-0 group-hover:translate-x-1 transition-transform",
                                                            children: "→"
                                                        })
                                                    ]
                                                })
                                            })
                                        ]
                                    })
                                }, p.id))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "relative bg-obsidian-950 text-bone-50 overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": true,
                        className: "absolute inset-0 pointer-events-none",
                        style: {
                            background: 'radial-gradient(ellipse 55% 40% at 100% 0%, rgba(220, 132, 32, 0.10), transparent 65%), radial-gradient(ellipse 45% 35% at 0% 100%, rgba(162, 88, 31, 0.12), transparent 60%)'
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "lg:col-span-5",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3 mb-6",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "h-px w-10 bg-clay-400",
                                                "aria-hidden": true
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "font-mono text-[10px] uppercase tracking-[0.35em] text-ember-300 font-medium",
                                                children: "Our standard"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("h2", {
                                        className: "font-display text-5xl lg:text-6xl leading-[0.95] text-bone-50",
                                        style: FRAUNCES_DISPLAY_BOLD,
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                style: FRAUNCES_DISPLAY_ITALIC,
                                                className: "text-clay-300",
                                                children: "How"
                                            }),
                                            ' ',
                                            "our standard works."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mt-8 text-bone-200 text-lg leading-[1.65] max-w-md",
                                        children: "Every product starts with a simple rule: it has to feel good in your hand, work the way it should, and look clean doing it. We focus on premium materials, smooth function, tight presentation, and details that make the product worth carrying."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "lg:col-span-7",
                                children: /*#__PURE__*/ _jsx("ol", {
                                    className: "space-y-0",
                                    children: PROCESS_STEPS.map((step, idx)=>/*#__PURE__*/ _jsxs("li", {
                                            className: "grid grid-cols-12 gap-4 items-baseline border-t border-obsidian-700 py-6",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "col-span-2 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-300/80",
                                                    children: String(idx + 1).padStart(2, '0')
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "col-span-10 font-mono text-[13px] text-bone-100 leading-[1.55]",
                                                    children: step.label
                                                })
                                            ]
                                        }, step.label))
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "bg-bone-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-3xl mx-auto px-6 py-24 lg:py-32 text-center",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "font-mono text-[10px] uppercase tracking-[0.35em] text-clay-600 font-medium mb-8",
                            children: "Start somewhere"
                        }),
                        /*#__PURE__*/ _jsxs("h2", {
                            className: "font-display text-obsidian-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]",
                            style: FRAUNCES_DISPLAY_BOLD,
                            children: [
                                "The catalog is",
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-clay-600",
                                    style: FRAUNCES_DISPLAY_ITALIC,
                                    children: "small"
                                }),
                                ". The standard is not."
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-12",
                            children: /*#__PURE__*/ _jsxs(Link, {
                                to: ROUTES.shop,
                                className: "group inline-flex items-center gap-3 bg-obsidian-900 text-bone-50 pl-6 pr-5 py-4 text-[11px] font-medium uppercase tracking-[0.3em] hover:bg-clay-600 transition-colors",
                                children: [
                                    "Visit the shop",
                                    /*#__PURE__*/ _jsx("span", {
                                        "aria-hidden": true,
                                        className: "inline-block translate-x-0 group-hover:translate-x-1 transition-transform",
                                        children: "→"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "mt-10 text-[11px] font-mono uppercase tracking-[0.3em] text-obsidian-500",
                            children: [
                                "Or, for wholesale —",
                                ' ',
                                /*#__PURE__*/ _jsx(Link, {
                                    to: ROUTES.contact,
                                    className: "text-clay-600 hover:text-clay-700 underline underline-offset-4",
                                    children: "contact the shop"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
/** Hard-coded brand voice — this page's copy is the brand, not the catalog. */ const PROCESS_STEPS = [
    {
        label: 'We design around real use — weight, grip, feel, storage, and everyday durability.'
    },
    {
        label: 'Materials and finishes are selected for a clean look, solid feel, and long-term use.'
    },
    {
        label: 'Each product is checked for fit, finish, movement, and overall presentation before it ships.'
    },
    {
        label: 'Orders are packed with care and shipped with the same attention we put into the product.'
    },
    {
        label: "If it doesn't meet the standard, it doesn't leave our hands."
    }
];
